/**
 * WHY THIS SUPPLY IS REVERSE CHARGE, IN ONE PLACE.
 *
 * The same fact was written out on three screens and in three PDFs, and every
 * one of them said the same wrong thing:
 *
 *     "He is not GST registered, so he charges nothing..."
 *
 * That was a safe shorthand while "reverse charge" and "unregistered" meant
 * the same thing in this system. They stopped meaning the same thing in 0146.
 * Entry 14 of Notification 13/2017-CT(R) — security personnel supplied by
 * anyone other than a body corporate — makes the RECIPIENT liable whoever the
 * supplier is, and BIPLAB SHARMA is registered: his GSTIN is printed on the
 * same page as the sentence denying he has one.
 *
 * Six copies were fixed one at a time over two days. This is the seventh
 * attempt to stop there being a seventh. The PDFs share
 * supabase/functions/_shared/reverse-charge-note.ts; the screens share this.
 * Two files because they cannot import each other — one runs in Deno on the
 * server, one in the browser — but they say the same thing and each one's
 * header points at the other.
 *
 * WHAT DIFFERS BETWEEN A REGISTERED AND AN UNREGISTERED SUPPLIER, and it is
 * not a nicety:
 *
 *   UNREGISTERED — he has no GSTIN and cannot raise a tax invoice at all.
 *   OVAL raises the self-invoice under section 31(3)(f) within thirty days.
 *
 *   REGISTERED — he raises his own invoice and rule 46(p) requires him to
 *   mark it "payable on reverse charge". OVAL must NOT raise a self-invoice;
 *   two documents for one supply declare the liability twice.
 */

export interface ReverseChargeFacts {
  /** Is the CONTRACTOR registered. Not OVAL, which always is. */
  registered: boolean
  /** "13/2017-CT(R)", when the screen has been told. */
  notification?: string | null
  /** "14". */
  entryNo?: string | null
}

/** "Notification 13/2017-CT(R) entry 14", or null when nothing was recorded. */
export function rcmAuthority(f: ReverseChargeFacts): string | null {
  if (!f.notification || !f.entryNo) return null
  return `Notification ${f.notification} entry ${f.entryNo}`
}

/**
 * The clause that explains the supplier's position — the half of the sentence
 * that was wrong everywhere. The caller supplies the rest, because a work
 * order, a bill and a certificate each need their own framing around it.
 */
export function rcmSupplierClause(f: ReverseChargeFacts): string {
  const authority = rcmAuthority(f)

  if (f.registered) {
    return authority
      ? `He is registered — that does not decide it. ${authority} makes the `
        + 'recipient liable whoever the supplier is, so he raises his own invoice '
        + 'marked as payable on reverse charge under rule 46(p) and must not '
        + 'charge the tax on it.'
      : 'He is registered, but this supply is one the recipient is liable for, so '
        + 'he raises his own invoice marked as payable on reverse charge under '
        + 'rule 46(p) and must not charge the tax on it.'
  }

  return authority
    ? `He is not registered under GST and cannot charge it. ${authority}.`
    : 'He is not registered under GST, so he charges nothing.'
}

/**
 * Whether OVAL must raise a self-invoice for this supply. Section 31(3)(f)
 * reaches only an unregistered supplier; a registered one issues his own.
 */
export const needsSelfInvoice = (f: ReverseChargeFacts): boolean => !f.registered
